import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Clock, KeyRound, Loader2, RefreshCw, ShieldCheck } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { bankConsentsAPI } from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';
import { toast } from 'sonner';

/**
 * Bank consents (top of the Integrations page).
 *
 * PSD2 consents die every 90-180 days and banks revoke them silently. Before
 * this panel the page rendered each provider's stored `consent_status`, which
 * is stamped at connect time and never revisited, so lapsed connections read
 * "consent active" and the only re-consent route was to re-pick the same
 * institution inside Configure. This panel derives the state (expiry, feed
 * staleness, provider flags) and puts one Re-consent button on each row.
 *
 * Backend: GET /api/integrations/bank-consents.
 */

const STATE_BADGE = {
  healthy: { label: 'authorised', variant: 'default', Icon: CheckCircle2 },
  expiring_soon: { label: 'expiring soon', variant: 'secondary', Icon: Clock },
  expired: { label: 'consent expired', variant: 'destructive', Icon: AlertTriangle },
  reconsent_required: { label: 'reconsent required', variant: 'destructive', Icon: AlertTriangle },
  no_data: { label: 'no data arriving', variant: 'destructive', Icon: AlertTriangle },
  disconnected: { label: 'not connected', variant: 'outline', Icon: AlertTriangle },
  credentials: { label: 'credentials', variant: 'secondary', Icon: KeyRound },
};

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function fmtAge(hours) {
  if (hours === null || hours === undefined) return 'never';
  if (hours < 48) return `${hours.toFixed(0)}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function ExpiryCell({ row }) {
  if (!row.consent_expires_at) {
    return <span className="text-muted-foreground">{row.expiry_basis === 'not_applicable' ? 'n/a' : 'unknown'}</span>;
  }
  const days = row.days_until_expiry;
  const late = days !== null && days !== undefined && days <= 14;
  return (
    <span className={late ? 'text-amber-600 dark:text-amber-400' : ''}>
      {fmtDate(row.consent_expires_at)}
      {days !== null && days !== undefined && (
        <span className="text-muted-foreground"> ({days > 0 ? `${Math.round(days)}d left` : `${Math.abs(Math.round(days))}d ago`})</span>
      )}
      {row.expiry_basis === 'assumed' && (
        <span className="text-muted-foreground" title="The provider does not return an expiry date; derived from the connection date plus the standard PSD2 grant length."> · estimated</span>
      )}
    </span>
  );
}

export function BankConsentsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: payload } = await bankConsentsAPI.get();
      setData(payload);
    } catch (err) {
      toast.error(`Bank consents failed to load: ${getErrorMessage(err)}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const reconsent = async (row) => {
    const key = `${row.source}:${row.connection_id}`;
    setBusyId(key);
    try {
      const res = await bankConsentsAPI.startReconsent(row.reconsent);
      const authUrl = res?.data?.auth_url || res?.data?.redirect_url || res?.data?.url;
      if (!authUrl) {
        toast.error(`No authorisation link came back for ${row.label}. ${res?.data?.error || ''}`);
        return;
      }
      // Leaving the app is the point: the bank hosts the consent screen.
      window.location.href = authUrl;
    } catch (err) {
      toast.error(`Re-consent could not start for ${row.label}: ${getErrorMessage(err)}`, { duration: 10000 });
    } finally {
      setBusyId(null);
    }
  };

  const rows = data?.rows || [];
  const actionRequired = data?.action_required || 0;

  return (
    <Card className={actionRequired > 0 ? 'border-amber-400 dark:border-amber-600' : ''} data-testid="bank-consents-panel">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" /> Bank consents
            </CardTitle>
            <CardDescription>
              Open-banking authorisations expire (and get revoked silently). This reads the real state of each
              connection — expiry, and whether data is still arriving — not the status stored at connect time.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading && !data && <p className="text-sm text-muted-foreground">Loading consent state…</p>}
        {data && (
          <>
            <p className="text-sm mb-3">
              {actionRequired > 0
                ? <span className="font-medium text-amber-600 dark:text-amber-400">{actionRequired} connection(s) need a fresh authorisation.</span>
                : <span className="text-muted-foreground">All bank connections are authorised and delivering data.</span>}
            </p>
            <div className="divide-y">
              {rows.map((row) => {
                const badge = STATE_BADGE[row.state] || { label: row.state, variant: 'outline', Icon: AlertTriangle };
                const key = `${row.source}:${row.connection_id}`;
                const manual = !row.reconsent || row.reconsent.method === 'MANUAL';
                return (
                  <div key={key} className="py-3 flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{row.label}</span>
                        <Badge variant={badge.variant} className="text-xs">
                          <badge.Icon className="w-3 h-3 mr-1" />{badge.label}
                        </Badge>
                        <span className="text-xs text-muted-foreground">{row.source_label}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{row.reason}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {row.account_count} account(s) · last data {fmtAge(row.last_synced_age_hours)} · consent until{' '}
                        <ExpiryCell row={row} />
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      {manual ? (
                        <span className="text-xs text-muted-foreground max-w-[14rem] block text-right">{row.reconsent?.note}</span>
                      ) : (
                        <Button
                          variant={row.needs_reconsent ? 'default' : 'outline'}
                          size="sm"
                          disabled={busyId === key}
                          title={row.reconsent?.note}
                          data-testid={`reconsent-${key}`}
                          onClick={() => reconsent(row)}
                        >
                          {busyId === key
                            ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                            : <RefreshCw className="w-3 h-3 mr-1" />}
                          Re-consent
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
              {rows.length === 0 && <p className="text-sm text-muted-foreground">No bank connections configured.</p>}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default BankConsentsPanel;
